import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Bauteil, ElektrolistePosition, Kunde } from '../types';
import kundeService from '../services/kundeService';
import { bauteilService, elektrolisteService } from '../services/elektrolisteService';
import { Icon, PageHeader, LoadingScreen, fehlerText } from '../components/ui';
import { EigenschaftenEditor, bekannteSchluessel } from '../components/EigenschaftenEditor';
import { ZurueckLink } from '../components/ZurueckLink';
import { markiereNeu } from '../utils/ankunft';
import { DatumFeld } from '../components/DatumZeit';

const heute = () => new Date().toLocaleDateString('sv-SE'); // YYYY-MM-DD in Ortszeit

const ElektrolisteForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [kunde, setKunde] = useState<Kunde | null>(null);
  const [katalog, setKatalog] = useState<Bauteil[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    kundeId: Number(searchParams.get('kundeId')) || 0,
    datum: heute(),
    titel: '',
    einleitung: '',
    schlusstext: '',
    positionen: [] as ElektrolistePosition[],
  });

  useEffect(() => {
    const laden = async () => {
      try {
        const [bauteile, liste, standard] = await Promise.all([
          bauteilService.getAll(),
          isEditMode ? elektrolisteService.getById(Number(id)) : Promise.resolve(null),
          isEditMode ? Promise.resolve(null) : elektrolisteService.getStandardtexte(),
        ]);
        setKatalog(bauteile);
        const kundeId = liste?.kundeId ?? form.kundeId;
        if (!kundeId) throw new Error('Kein Kunde');
        setKunde(await kundeService.getById(kundeId));
        if (liste) {
          setForm({
            kundeId: liste.kundeId,
            datum: liste.datum,
            titel: liste.titel,
            einleitung: liste.einleitung || '',
            schlusstext: liste.schlusstext || '',
            positionen: liste.positionen,
          });
        } else if (standard) {
          setForm(f => ({ ...f, titel: standard.titel, einleitung: standard.einleitung, schlusstext: standard.schlusstext }));
        }
      } catch (err) {
        toast.error(fehlerText(err, 'Elektroliste konnte nicht geladen werden'));
        navigate(-1);
      } finally {
        setLoading(false);
      }
    };
    laden();
    // Lädt einmal je Liste; `form.kundeId` stammt beim Anlegen aus der URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const vorschlaege = useMemo(
    () => bekannteSchluessel([...katalog.map(b => b.eigenschaften), ...form.positionen.map(p => p.eigenschaften)]),
    [katalog, form.positionen]
  );

  // Spalten so, wie sie im PDF erscheinen: Reihenfolge des ersten Auftretens, ohne Groß-/Kleinschreibungs-Doppel.
  const pdfSpalten = useMemo(() => {
    const gesehen = new Map<string, string>();
    form.positionen.flatMap(p => p.eigenschaften).forEach(e => {
      const s = e.schluessel.trim();
      if (s && !gesehen.has(s.toLowerCase())) gesehen.set(s.toLowerCase(), s);
    });
    return Array.from(gesehen.values());
  }, [form.positionen]);

  const setPositionen = (positionen: ElektrolistePosition[]) => setForm(f => ({ ...f, positionen }));

  const aendere = (index: number, aenderung: Partial<ElektrolistePosition>) =>
    setPositionen(form.positionen.map((p, i) => (i === index ? { ...p, ...aenderung } : p)));

  const verschiebe = (index: number, richtung: -1 | 1) => {
    const ziel = index + richtung;
    if (ziel < 0 || ziel >= form.positionen.length) return;
    const neu = [...form.positionen];
    [neu[index], neu[ziel]] = [neu[ziel], neu[index]];
    setPositionen(neu);
  };

  const ausKatalog = (bauteilId: string) => {
    const bauteil = katalog.find(b => b.id === Number(bauteilId));
    if (!bauteil) return;
    setPositionen([...form.positionen, {
      titel: bauteil.titel,
      anzahl: 1,
      eigenschaften: bauteil.eigenschaften.map(e => ({ ...e })),
    }]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    const daten = {
      ...form,
      positionen: form.positionen.map(p => ({ ...p, anzahl: Math.max(1, Math.round(p.anzahl) || 1) })),
    };
    try {
      if (isEditMode) {
        await elektrolisteService.update(Number(id), daten);
        markiereNeu(`elektroliste-${id}`);
        toast.success('Elektroliste gespeichert');
      } else {
        const angelegt = await elektrolisteService.create(daten);
        markiereNeu(`elektroliste-${angelegt.id}`);
        toast.success('Elektroliste angelegt');
      }
      // Zurück dorthin, woher man kam (meist der Kunde); direkt geöffnet → zum Kunden.
      const idx = (window.history.state as { idx?: number } | null)?.idx;
      if (idx !== undefined && idx > 0) navigate(-1);
      else navigate(`/kunden/${form.kundeId}`, { replace: true });
    } catch (err) {
      toast.error(fehlerText(err, 'Elektroliste konnte nicht gespeichert werden'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <LoadingScreen />;
  if (!kunde) return null;

  const kundeName = [kunde.vorname, kunde.nachnameFirma].filter(Boolean).join(' ');

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto">
      <div className="mb-3">
        <ZurueckLink fallback={`/kunden/${kunde.id}`} fallbackLabel="Zurück zum Kunden" />
      </div>
      <PageHeader title={isEditMode ? 'Elektroliste bearbeiten' : 'Elektroliste anlegen'} subtitle={kundeName} />

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white rounded-lg shadow p-4 md:p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-1">Titel</label>
              <input type="text" required value={form.titel}
                onChange={(e) => setForm({ ...form, titel: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-lg text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Datum</label>
              <DatumFeld required value={form.datum}
                onChange={(v) => setForm({ ...form, datum: v })}
                className="w-full p-2 border border-gray-300 rounded-lg text-sm" />
            </div>
          </div>
          <label className="block text-sm font-medium mb-1">Text über der Tabelle</label>
          <textarea rows={4} value={form.einleitung}
            onChange={(e) => setForm({ ...form, einleitung: e.target.value })}
            className="w-full p-2 border border-gray-300 rounded-lg text-sm" />
        </div>

        <div className="bg-white rounded-lg shadow p-4 md:p-6">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
            <h2 className="text-lg font-semibold">
              Bauteile
              <span className="ml-2 text-sm font-normal text-gray-400">({form.positionen.length})</span>
            </h2>
          </div>
          {form.positionen.length > 0 && (
            <p className="text-xs text-gray-500 mb-4">
              Spalten im PDF: {[
                'Bezeichnung',
                // Wie im PDF: „Anzahl“ nur, wenn ein Bauteil mehr als einmal verbaut wird.
                ...(form.positionen.some(p => Number(p.anzahl) !== 1) ? ['Anzahl'] : []),
                ...pdfSpalten,
              ].join(', ')}
            </p>
          )}

          <div className="space-y-3 mb-4">
            {form.positionen.map((p, i) => (
              <div key={i} className="ui-aufklappen rounded-lg border border-gray-200 p-3 md:p-4">
                <div className="flex gap-2 mb-3">
                  <input type="text" required value={p.titel}
                    onChange={(e) => aendere(i, { titel: e.target.value })}
                    placeholder="Bezeichnung"
                    aria-label="Bezeichnung"
                    className="flex-1 min-w-0 p-2 border border-gray-300 rounded-lg text-sm font-semibold" />
                  <input type="number" min={1} step={1} required value={p.anzahl}
                    onChange={(e) => aendere(i, { anzahl: Number(e.target.value) })}
                    aria-label="Anzahl"
                    title="Anzahl"
                    className="w-16 p-2 border border-gray-300 rounded-lg text-sm text-center" />
                  <div className="flex flex-shrink-0">
                    <button type="button" onClick={() => verschiebe(i, -1)} disabled={i === 0}
                      className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg disabled:opacity-30 disabled:hover:bg-transparent"
                      aria-label="Nach oben" title="Nach oben">
                      <Icon.PfeilHoch className="w-4 h-4" />
                    </button>
                    <button type="button" onClick={() => verschiebe(i, 1)} disabled={i === form.positionen.length - 1}
                      className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg disabled:opacity-30 disabled:hover:bg-transparent"
                      aria-label="Nach unten" title="Nach unten">
                      <Icon.PfeilRunter className="w-4 h-4" />
                    </button>
                    <button type="button" onClick={() => setPositionen(form.positionen.filter((_, j) => j !== i))}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                      aria-label="Bauteil entfernen" title="Bauteil entfernen">
                      <Icon.Trash className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <EigenschaftenEditor
                  eigenschaften={p.eigenschaften}
                  onChange={(eigenschaften) => aendere(i, { eigenschaften })}
                  vorschlaege={vorschlaege}
                />
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-2">
            <select
              value=""
              onChange={(e) => ausKatalog(e.target.value)}
              className="flex-1 min-w-[14rem] p-2 border border-gray-300 rounded-lg text-sm bg-white"
              aria-label="Bauteil aus dem Katalog hinzufügen"
            >
              <option value="">{katalog.length ? 'Bauteil aus dem Katalog hinzufügen …' : 'Noch keine Bauteile im Katalog'}</option>
              {katalog.map(b => <option key={b.id} value={b.id}>{b.titel}</option>)}
            </select>
            <button type="button"
              onClick={() => setPositionen([...form.positionen, { titel: '', anzahl: 1, eigenschaften: [] }])}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
              <Icon.Plus className="h-4 w-4" />Eigenes Bauteil
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4 md:p-6">
          <label className="block text-sm font-medium mb-1">Text unter der Tabelle</label>
          <textarea rows={2} value={form.schlusstext}
            onChange={(e) => setForm({ ...form, schlusstext: e.target.value })}
            className="w-full p-2 border border-gray-300 rounded-lg text-sm" />
        </div>

        <div className="flex justify-end gap-4">
          <button type="button" onClick={() => navigate(-1)}
            className="px-6 py-3 text-gray-600 bg-gray-200 rounded-lg hover:bg-gray-300">
            Abbrechen
          </button>
          <button type="submit" disabled={submitting}
            className="px-6 py-3 bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50">
            {submitting ? 'Wird gespeichert …' : isEditMode ? 'Speichern' : 'Elektroliste anlegen'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ElektrolisteForm;
